import { Fragment, ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  showCloseButton?: boolean;
  /** Permite cerrar haciendo clic fuera del modal */
  closeOnOutsideClick?: boolean;
  /** Permite cerrar con la tecla Escape */
  closeOnEscape?: boolean;
}

const sizeClasses = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  full: 'sm:max-w-full sm:m-4',
};

/**
 * Modal - Componente de modal con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Dark mode: bg-gray-800/50 para backdrop
 * - Panel: rounded-lg con shadow-xl
 * - Transiciones suaves
 * - Close button mejorado
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  closeOnOutsideClick = true,
  closeOnEscape = true,
}: ModalProps) {
  const handleClose = () => {
    if (closeOnOutsideClick || closeOnEscape) {
      onClose();
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleClose}
        static={!closeOnEscape && !closeOnOutsideClick}
      >
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Panel */}
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={cn(
                  'relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all',
                  'bg-white dark:bg-secondary-800',
                  'sm:my-8 sm:w-full',
                  sizeClasses[size]
                )}
              >
                {/* Header */}
                {(title || showCloseButton) && (
                  <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100 dark:border-white/5">
                    {title && (
                      <DialogTitle
                        as="h3"
                        className="text-lg font-semibold text-secondary-900 dark:text-white"
                      >
                        {title}
                      </DialogTitle>
                    )}
                    {showCloseButton && (
                      <button
                        type="button"
                        onClick={onClose}
                        className={cn(
                          'rounded-md p-1 -mr-1',
                          'text-secondary-400 hover:text-secondary-500',
                          'dark:text-secondary-500 dark:hover:text-white',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500',
                          'dark:focus-visible:ring-offset-secondary-800'
                        )}
                        aria-label="Close"
                      >
                        <XMarkIcon className="size-5" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}

                {/* Content */}
                <div className="px-6 py-4">
                  {children}
                </div>

                {/* Footer */}
                {footer && (
                  <div className="flex justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50/50 dark:border-white/5 dark:bg-secondary-900/50">
                    {footer}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
